import axios from "axios";
import { SERVER_URL } from "../../../databucket";

export const WA_FILTERS = [
  { key: "unassigned", label: "Unassigned", countKey: "unassigned", unreadKey: "unassignedUnread" },
  { key: "mine", label: "Mine", countKey: "mine", unreadKey: "mineUnread" },
  { key: "all", label: "All", countKey: "all" },
  { key: "pending", label: "Pending", countKey: "pending" },
  { key: "closed", label: "Closed", countKey: "closed" },
];

export const WA_STATUS_LABELS = { open: "Open", pending: "Pending", closed: "Closed" };

// Friendly copy for server rule codes (whatsapp/guardrails.js).
export const WA_ERROR_HINTS = {
  WINDOW_CLOSED: "The 24-hour reply window is closed — send an approved template instead.",
  NO_OPT_IN: "No opt-in on record for this number. Record consent first.",
  OPTED_OUT: "This contact opted out (STOP).",
  MARKETING_NOT_ALLOWED: "Marketing templates need a recorded opt-in (and, if the admin rule is on, prior email engagement).",
  TEMPLATE_COOLDOWN: "Wait before re-sending a template to a contact who has not replied (set in WhatsApp Admin → Templates).",
  TEMPLATE_LIMIT: "Too many templates in a row without a reply (set in WhatsApp Admin → Templates).",
  RATE_LIMITED: "Hourly sending limit reached.",
  NOT_CONFIGURED: "WhatsApp is not configured on the server yet.",
  FORBIDDEN: "You don't have permission for this conversation.",
};

/** One line for a send error: the server's message, else the friendly hint for its code. */
export const sendErrorText = (error) => error?.message || WA_ERROR_HINTS[error?.code] || "Something went wrong";

export const gqlErrorMessage = (err) =>
  err?.graphQLErrors?.[0]?.message || err?.networkError?.result?.errors?.[0]?.message || err?.message || "Something went wrong";

export const initialsOf = (name, fallback = "?") => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

export const displayNameOf = (c) => c?.contactName || c?.profileName || c?.displayNumber || (c?.waId ? `+${c.waId}` : "Unknown");

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** List timestamp: "3:42 PM", "Yesterday", "Mon", "Oct 12". */
export const listTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return "Yesterday";
  if (now - d < 6 * 24 * 3600 * 1000) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric", ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) });
};

export const bubbleTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

export const dayLabel = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return "Today";
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) });
};

export const fullTime = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

/** "6h 12m" until the 24h window closes, or null when closed. */
export const windowRemaining = (expiresAt, now = Date.now()) => {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export const isWindowOpen = (c, now = Date.now()) => !!(c?.windowExpiresAt && new Date(c.windowExpiresAt).getTime() > now);

export const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Signed media links expire (server: 1h); refresh this long before they do. */
export const MEDIA_REFRESH_AHEAD_MS = 10 * 60 * 1000;

/** Expiry (ms) encoded in a signed media link's `e` param, or null. */
export const mediaLinkExpiresAt = (url) => {
  const m = /[?&]e=(\d+)/.exec(String(url || ""));
  return m ? Number(m[1]) * 1000 : null;
};

/** Media links come back as host-less paths; prefix this app's API base (absolute URLs pass through). */
export const mediaUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SERVER_URL.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
};

/** Upload a file and queue it as an outbound WhatsApp media message. */
export const uploadWhatsAppMedia = async ({ conversationId, file, caption, onProgress }) => {
  const form = new FormData();
  form.append("conversationId", conversationId);
  form.append("caption", caption || "");
  form.append("file", file);
  const token = localStorage.getItem("token");
  try {
    const { data } = await axios.post(`${SERVER_URL}/api/whatsapp/media`, form, {
      // Same file ships in web and Desktop (Rule 1) — report the real platform.
      headers: { Authorization: token ? `Bearer ${token}` : "", "x-app-platform": typeof window !== "undefined" && window.electronAPI ? "DESKTOP" : "WEB" },
      withCredentials: true,
      onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
    });
    return data;
  } catch (err) {
    const data = err?.response?.data;
    return { ok: false, code: data?.code || "UPLOAD_FAILED", message: data?.message || err.message || "Upload failed" };
  }
};

export const firstNameOf = (full) => String(full || "").trim().split(/\s+/)[0] || "";

/**
 * Auto-fill template variables from context.
 * ctx: { contactName, recordName, senderName }
 */
export const autoFillValue = (source, ctx) => {
  switch (source) {
    case "contact_first_name": return firstNameOf(ctx.contactName);
    case "contact_full_name": return String(ctx.contactName || "").trim();
    case "record_name": return String(ctx.recordName || "").trim();
    case "sender_first_name": return firstNameOf(ctx.senderName);
    case "sender_full_name": return String(ctx.senderName || "").trim();
    default: return "";
  }
};

export const VARIABLE_SOURCE_LABELS = {
  contact_first_name: "Contact first name",
  contact_full_name: "Contact full name",
  record_name: "Agency / client name",
  sender_first_name: "My first name",
  sender_full_name: "My full name",
  custom: "Typed each time",
};

/** Render a template body with values substituted (preview). */
export const renderTemplate = (template, values) => {
  if (!template) return "";
  const sub = (text, component) =>
    String(text || "").replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (whole, key) => {
      const idx = template.variables.findIndex((v) => v.component === component && v.key === key);
      return idx >= 0 && values[idx] ? values[idx] : whole;
    });
  return [sub(template.headerText, "header"), sub(template.bodyText, "body"), template.footerText].filter(Boolean).join("\n\n");
};
